import { Pagination, Space, Table, Tooltip } from 'antd';
import Column from 'antd/es/table/Column';
import dayjs from 'dayjs';
import { FC } from 'react';
import { useSelector } from 'react-redux';
import {
	DeleteActionSvg,
	EditActionSvg,
	SortBottomIcon,
	SortTopIcon,
	TableNextIcon,
	TablePreviousIcon,
} from 'utils/svgs';
import { checkPermission } from 'utils/utils';
import SearchAndFilter from '../SearchAndFilter';
import styles from './index.module.scss';
import './index.scss';
import { CustomUserTableProps } from './type';
import type { DraftMedicineRequestRow } from '../types';

const CustomTable: FC<CustomUserTableProps> = (props) => {
	const {
		dataSource,
		handleTableChange,
		onShowSizeChange,
		isLoading,
		search,
		totalRecords,
		performSearchHandler,
		setSearch,
		selectedDate,
		setSelectedDate,
		onEditDraftRequest,
		onMapDraftRequest,
		onDeleteDraftRequest,
		currentPage,
		pageSize,
		statusFilter,
		onStatusFilterChange,
		categoryFilter,
		onCategoryFilterChange,
		categories,
		searchField,
		onSearchFieldChange,
	} = props;

	const medicinesPermissions = useSelector((state: any) => state?.userProfile);
	const isAdmin = medicinesPermissions?.data?.isAdmin === true;

	// Edit / Map: only DraftMedicinesRequest edit (or module "all")
	const canEdit = checkPermission(medicinesPermissions?.data?.permissions, {
		permissionName: 'DraftMedicinesRequest',
		permission: ['edit'],
	});

	// Delete button: only DraftMedicinesRequest delete (or module "all")
	const canDelete = checkPermission(medicinesPermissions?.data?.permissions, {
		permissionName: 'DraftMedicinesRequest',
		permission: ['delete'],
	});

	const statusClassFor = (value: string) => {
		const u = value?.toUpperCase();
		if (u === 'PENDING') return styles.status__pending;
		if (u === 'REJECTED') return styles.status__pending;
		if (u === 'DELETED') return styles.status__pending;
		if (u === 'APPROVED') return styles.status__approved;
		return styles.status__approved;
	};

	const isDeletedView = statusFilter === 'DELETED';

	const renderEllipsisCell = (text: string) => (
		<Tooltip title={text} placement="topLeft">
			<span
				style={{
					display: 'inline-block',
					maxWidth: '100%',
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					whiteSpace: 'nowrap',
					verticalAlign: 'bottom',
				}}
			>
				{text}
			</span>
		</Tooltip>
	);

	return (
		<>
			<div className={`${styles['body']} user-table`}>
				<SearchAndFilter
					performSearchHandler={performSearchHandler}
					searchValue={search}
					title={'Draft Medicines Request'}
					setSearch={setSearch}
					selectedDate={selectedDate}
					setSelectedDate={setSelectedDate}
					totalRecords={totalRecords}
					statusFilter={statusFilter}
					onStatusFilterChange={onStatusFilterChange}
					categoryFilter={categoryFilter}
					onCategoryFilterChange={onCategoryFilterChange}
					categories={categories}
					showAddButton={false}
					searchField={searchField}
					onSearchFieldChange={onSearchFieldChange}
				/>

				<Table<DraftMedicineRequestRow>
					rowKey={(record) => record?.id}
					dataSource={dataSource}
					pagination={false}
					loading={isLoading}
					onChange={handleTableChange}
					scroll={{ y: 'calc(100vh - 300px)' }}
				>
					<Column<DraftMedicineRequestRow>
						title={<p style={{ padding: '0 10px' }}>Medicine Name</p>}
						dataIndex="medicineName"
						key="medicineName"
						className="user-column bg-white"
						width="14%"
						sorter
						sortDirections={['ascend', 'descend', 'ascend']}
						render={(value: string) => (value ? value : '-')}
						sortIcon={({ sortOrder }) => (
							<>
								<SortTopIcon fill={sortOrder === 'ascend' ? '#949494' : undefined} />
								<SortBottomIcon fill={sortOrder === 'descend' ? '#949494' : undefined} />
							</>
						)}
					/>
					<Column<DraftMedicineRequestRow>
						title="Pharmacy Code"
						dataIndex="pharmacyCode"
						key="pharmacyCode"
						className="bg-white"
						width="12%"
						ellipsis
						render={(_value, record) => {
							const text = record.pharmacyCode?.trim() || '-';
							return renderEllipsisCell(text);
						}}
					/>
					{isAdmin && (
						<Column<DraftMedicineRequestRow>
							title="Pharmacy Name"
							dataIndex="pharmacyName"
							key="pharmacyName"
							className="bg-white"
							width="12%"
							ellipsis
							render={(_value, record) => {
								const text = record.pharmacyName?.trim() || '-';
								return renderEllipsisCell(text);
							}}
						/>
					)}
					<Column<DraftMedicineRequestRow>
						title="Status"
						dataIndex="status"
						key="status"
						className="bg-white"
						width="10%"
						render={(value: string) =>
							value ? <span className={statusClassFor(value)}>{value}</span> : '-'
						}
					/>
					{statusFilter === 'MAPPED' && (
						<Column<DraftMedicineRequestRow>
							title="Mapped To Medicine"
							key="mappedToMedicine"
							className={`bg-white ${styles['mapped-to-column']}`}
							width="18%"
							render={(_value, record) => {
								const name = record.mappedToMedicineName?.trim();
								const id = record.mappedToMedicineId?.trim();
								if (!name && !id) return '-';
								return (
									<div className={styles['mapped-to']}>
										<div className={styles['mapped-to__name']}>{name || '-'}</div>
										{id ? (
											<div className={styles['mapped-to__id']}>{id}</div>
										) : null}
									</div>
								);
							}}
						/>
					)}
					{isDeletedView && (
						<>
							<Column<DraftMedicineRequestRow>
								title="Deleted By"
								key="deletedBy"
								className="bg-white"
								width="14%"
								ellipsis
								render={(_value, record) => {
									const text =
										record.deletedByName?.trim() ||
										record.deletedBy?.trim() ||
										'-';
									return (
										<Tooltip title={text} placement="topLeft">
											<span
												style={{
													display: 'inline-block',
													maxWidth: '100%',
													overflow: 'hidden',
													textOverflow: 'ellipsis',
													whiteSpace: 'nowrap',
													verticalAlign: 'bottom',
												}}
											>
												{text}
											</span>
										</Tooltip>
									);
								}}
							/>
							<Column<DraftMedicineRequestRow>
								title="Deleted At"
								dataIndex="deletedAt"
								key="deletedAt"
								className="bg-white"
								width="12%"
								render={(value: string) =>
									value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-'
								}
							/>
						</>
					)}
					{!isDeletedView && (
						<>
							<Column<DraftMedicineRequestRow>
								title="Created at"
								dataIndex="createdAt"
								key="createdAt"
								className="bg-white"
								width="12%"
								render={(value: string) =>
									value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-'
								}
							/>
							<Column<DraftMedicineRequestRow>
								title="Updated at"
								dataIndex="updatedAt"
								key="updatedAt"
								className="bg-white"
								width="12%"
								render={(value: string) =>
									value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-'
								}
							/>
						</>
					)}

					{(canEdit || canDelete) && !isDeletedView && (
						<Column<DraftMedicineRequestRow>
							title="Action"
							key="action"
							className="bg-white"
							width="12%"
							render={(_value, record) => {
								const isPending = record.status?.toUpperCase() === 'PENDING';
								return (
									<Space size={20}>
										{canEdit && (
											<div
												className="cursor-pointer flex align-center justify-center rounded-action-btn"
												onClick={() => onEditDraftRequest(record)}
												role="presentation"
											>
												<EditActionSvg />
											</div>
										)}
										{canEdit && isPending && (
											<span
												className="cursor-pointer"
												style={{ color: '#1677ff', fontWeight: 500 }}
												onClick={() => onMapDraftRequest(record)}
												role="presentation"
											>
												Map
											</span>
										)}
										{canDelete && isPending && (
											<div
												className="cursor-pointer flex align-center justify-center rounded-action-btn"
												onClick={() => onDeleteDraftRequest(record)}
												role="presentation"
											>
												<DeleteActionSvg />
											</div>
										)}
									</Space>
								);
							}}
						/>
					)}
				</Table>

				<div className={styles['pagination__wrapper']}>
					<Pagination
						current={currentPage}
						pageSize={pageSize}
						showSizeChanger
						onChange={onShowSizeChange}
						total={totalRecords}
						itemRender={(page, type, originalElement) => (
							<div className={styles['pagination']}>
								{type === 'prev' ? (
									<button
										type="button"
										className={`${styles['pagination']} ${styles['pagination__button']}`}
									>
										<TablePreviousIcon />
										<p className={styles['pagination__previous']}>Previous</p>
									</button>
								) : type === 'next' ? (
									<button
										type="button"
										className={`${styles['pagination']} ${styles['pagination__button']}`}
									>
										<p className={styles['pagination__next']}>Next</p>
										<TableNextIcon />
									</button>
								) : (
									originalElement
								)}
							</div>
						)}
					/>
				</div>
			</div>
		</>
	);
};

export default CustomTable;
